/**
 * Confirmation dialog for live ODM 837P submission.
 *
 * Flow:
 *   1. Hits /api/odm-sftp/submit-claims/preview to validate the selected claim_ids.
 *   2. Shows the user a summary: eligible claims with $ + skipped claims with reasons.
 *   3. On confirm, hits /api/odm-sftp/submit-claims to actually upload to ODM SFTP.
 */
import React, { useEffect, useState } from 'react';
import { formatDate } from '../../lib/utils';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Spinner, PaperPlaneTilt, CheckCircle, Warning } from '@phosphor-icons/react';
import PreflightFindings from './PreflightFindings';
import ClaimLinePicker from './ClaimLinePicker';

const formatCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

export const SubmitToOdmDialog = ({ open, claimIds, onClose, onSubmitted }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  // claim_id → { payer_type, payer_program, service_code } overrides set
  // via the per-claim service-line picker. Sent with /submit-claims so
  // the backend applies + persists them right before EDI generation.
  const [overrides, setOverrides] = useState({});

  const setOverrideFor = (claimId, value) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (!value) delete next[claimId];
      else next[claimId] = value;
      return next;
    });
  };

  useEffect(() => {
    if (!open || !claimIds || claimIds.length === 0) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setResult(null);
      setOverrides({});
      try {
        const res = await api.post('/odm-sftp/submit-claims/preview', { claim_ids: claimIds });
        if (!cancelled) setPreview(res.data);
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.detail || 'Failed to validate claims');
          onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [open, claimIds, onClose]);

  const handleClose = () => {
    setPreview(null);
    setResult(null);
    setOverrides({});
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post('/odm-sftp/submit-claims', {
        claim_ids: claimIds,
        overrides,
      });
      setResult(res.data);
      toast.success(`Submitted ${res.data.submitted_count} claim${res.data.submitted_count === 1 ? '' : 's'} to ODM`);
      onSubmitted?.(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Submission failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Result view (after successful submission)
  if (result) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg" data-testid="odm-submit-result">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle size={22} weight="fill" className="text-emerald-600" />
              Submitted to Ohio Medicaid
            </DialogTitle>
            <DialogDescription>
              Your 837P file has been delivered to the ODM CERT SFTP. ODM will
              respond with a 999 acknowledgment (typically within hours) and a
              277CA claim status (typically within days). Both will appear here
              automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm bg-slate-50 rounded-md p-4 border border-slate-200">
            <div className="flex justify-between">
              <span className="text-slate-500">Filename</span>
              <span className="font-mono text-xs text-slate-900 truncate max-w-[280px]" title={result.filename}>
                {result.filename}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Batch ID</span>
              <span className="font-mono text-xs text-slate-900">{result.batch_id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Claims submitted</span>
              <span className="font-semibold text-emerald-700">{result.submitted_count}</span>
            </div>
            {result.skipped_count > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-500">Claims skipped</span>
                <span className="font-semibold text-amber-700">{result.skipped_count}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-slate-500">File size</span>
              <span className="text-slate-900">{result.file_size} bytes</span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleClose} className="bg-indigo-900 hover:bg-indigo-800" data-testid="odm-submit-result-close">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Preview view (before user confirms)
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="odm-submit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PaperPlaneTilt size={22} className="text-indigo-700" />
            Send Claims to Ohio Medicaid
          </DialogTitle>
          <DialogDescription>
            Live submission to ODM CERT environment via SFTP. Once sent, claims
            move to <strong>Submitted</strong> status and ODM begins
            processing. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size={28} className="animate-spin text-indigo-600" />
            <span className="ml-3 text-sm text-slate-500">Validating claims…</span>
          </div>
        )}

        {!loading && preview && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-emerald-700">{preview.eligible_count}</p>
                <p className="text-xs text-emerald-700 mt-0.5">eligible</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-amber-700">{preview.skipped_count}</p>
                <p className="text-xs text-amber-700 mt-0.5">skipped</p>
              </div>
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-indigo-900">{formatCurrency(preview.total_charge)}</p>
                <p className="text-xs text-indigo-700 mt-0.5">total billed</p>
              </div>
            </div>

            {/* Pre-flight aggregate banner — surfaces Phase B rule findings */}
            {preview.preflight_summary && (preview.preflight_summary.warnings > 0 || preview.preflight_summary.blockers > 0) && (
              <div
                className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900"
                data-testid="preflight-summary-banner"
              >
                <Warning size={16} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold">Pre-flight checks found issues</p>
                  <p className="mt-0.5">
                    {preview.preflight_summary.warnings > 0 && (
                      <>{preview.preflight_summary.warnings} warning{preview.preflight_summary.warnings === 1 ? '' : 's'}</>
                    )}
                    {preview.preflight_summary.warnings > 0 && preview.preflight_summary.blockers > 0 && ' · '}
                    {preview.preflight_summary.blockers > 0 && (
                      <strong>{preview.preflight_summary.blockers} blocker{preview.preflight_summary.blockers === 1 ? '' : 's'}</strong>
                    )}
                    . Expand &ldquo;Pre-flight checks&rdquo; below each claim to review. Warnings still allow submission; blockers will be skipped.
                  </p>
                </div>
              </div>
            )}

            {/* Eligible list */}
            {preview.eligible.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                  <CheckCircle size={14} weight="fill" className="text-emerald-600" />
                  Eligible to send ({preview.eligible.length})
                </h4>
                <div className="border border-emerald-200 rounded-md divide-y divide-emerald-100 max-h-72 overflow-y-auto bg-emerald-50/30">
                  {preview.eligible.map((c) => (
                    <div
                      key={c.claim_id}
                      className="px-3 py-2 text-xs"
                      data-testid={`eligible-claim-${c.claim_id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-mono text-slate-900 truncate">{c.claim_number}</p>
                          <p className="text-slate-500 mt-0.5 truncate">
                            {c.patient_name} · {formatDate(c.service_date)} · {c.units}u
                          </p>
                        </div>
                        <span className="font-semibold text-emerald-700 whitespace-nowrap">
                          {formatCurrency(c.charge)}
                        </span>
                      </div>
                      <ClaimLinePicker
                        claim={c}
                        override={overrides[c.claim_id]}
                        onOverride={setOverrideFor}
                      />
                      <PreflightFindings preflight={c.preflight} claimId={c.claim_id} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Skipped list */}
            {preview.skipped.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                  <Warning size={14} weight="fill" className="text-amber-600" />
                  Will be skipped ({preview.skipped.length})
                </h4>
                <div className="border border-amber-200 rounded-md divide-y divide-amber-100 max-h-48 overflow-y-auto bg-amber-50/30">
                  {preview.skipped.map((s) => (
                    <div
                      key={s.claim_id}
                      className="px-3 py-2 text-xs flex items-center justify-between gap-2"
                      data-testid={`skipped-claim-${s.claim_id}`}
                    >
                      <span className="font-mono text-slate-900 truncate">
                        {s.claim_number || s.claim_id.slice(0, 8)}
                      </span>
                      <Badge className="bg-amber-100 text-amber-800 text-[10px]">
                        {s.reason}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {preview.eligible_count === 0 && (
              <div className="text-center py-6 text-sm text-slate-500 bg-slate-50 rounded-md border border-slate-200">
                None of the selected claims are eligible. Resolve the issues above and try again.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting} data-testid="odm-submit-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || loading || !preview || preview.eligible_count === 0}
            className="bg-indigo-900 hover:bg-indigo-800"
            data-testid="odm-submit-confirm"
          >
            {submitting ? (
              <>
                <Spinner size={16} className="mr-2 animate-spin" />
                Uploading to ODM…
              </>
            ) : (
              <>
                <PaperPlaneTilt size={16} className="mr-2" />
                Send {preview?.eligible_count || 0} Claim{preview?.eligible_count === 1 ? '' : 's'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
